import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  inviteUser,
  inviteUsers,
  listUsers,
  resendInvitation,
  deleteUser,
  updateUserRole,
  updateUserProjectShare,
} from "../../service/api_service";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { BorderWidget, MainButton, UserAddModal, UserItemWidget, UserLoadingItemWidget, UserPendingItemWidget, UserProjectsModal, UserRemoveModal, UserRoleModal, UserTransferOwnerModal } from "../index";
import { Constants } from "../../utils/constants";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useSnackbar } from "notistack";

function Users({ jobStore, authStore }) {
  useEffect(() => {
    jobStore.setNavigation("users");
  }, [jobStore, authStore]);

  const currentUser = authStore.user;
  const currentUserRole = authStore.userRole;
  const { enqueueSnackbar } = useSnackbar();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransformOwnerModal, setShowTransformOwnerModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [confirmedUsers, setConfirmedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState([]);
  const [projects, setProjects] = useState([]);

  const resend = async (userId) => {
    setLoading(true);
    await resendInvitation(userId);
    enqueueSnackbar("Resent invitation successfully", { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } });
    await fetchData({ isLoad: true });
  };

  const fetchData = async ({ isLoad = false }) => {
    if (isLoad) setLoading(true);
    const users = await listUsers();
    console.log(users);
    setPendingUsers(users.filter((user) => user.hasOwnProperty("status") && user.status !== "CONFIRMED" && user.status !== null));
    setConfirmedUsers(users.filter((user) => !user.hasOwnProperty("status") || user.status === "CONFIRMED" || user.status == null).sort((a, b) => a.user_id.localeCompare(b.user_id)));
    setUsers(users);
    setLoading(false);
    users.map((user) => {
      if (user.role === "owner") {
        setProjects([...user.projects]);
      }
    });
  };

  useEffect(() => {
    fetchData({ isLoad: true });
  }, []);

  const onUsersInvite = async (items) => {
    setIsModalOpen(false);
    setLoading(true);
    console.log(items);
    // need to call endpoint
    await inviteUsers(items);
    enqueueSnackbar("Invited users successfully", { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } });
    fetchData({ isLoad: true });
  };

  const onUserDelete = async (userId) => {
    setLoadingProgress([...loadingProgress, userId]);
    try {
      await deleteUser(userId);
      enqueueSnackbar("Deleted user successfully", { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } });
      await fetchData({ isLoad: false });
    } finally {
      setLoadingProgress(
        loadingProgress.splice(loadingProgress.indexOf(userId), 1)
      );
    }
  }

  const onUserRoleUpdate = async (role, user) => {
    console.log(role, user);
    setShowChangeRoleModal(false);
    setLoadingProgress([...loadingProgress, user.user_id]);
    try {
      await updateUserRole({ role, user_id: user.user_id });
      enqueueSnackbar("Updated user role successfully", { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } });
      await fetchData({ isLoad: false });
    } finally {
      setLoadingProgress(
        loadingProgress.splice(loadingProgress.indexOf(user.user_id), 1)
      );
    }
  }

  const onUserProjectsUpdate = async (ps, user) => {
    console.log(ps, user);
    setShowProjectsModal(false);
    setLoadingProgress([...loadingProgress, user.user_id]);
    try {
      await updateUserProjectShare({ projects: ps, user_id: user.user_id });
      enqueueSnackbar("Updated user projects successfully", { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } });
      await fetchData({ isLoad: false });
    } finally {
      setLoadingProgress(
        loadingProgress.splice(loadingProgress.indexOf(user.user_id), 1)
      );
    }
  }

  const onUserTransferOwnerUpdate = async (user) => {
    console.log(user);
    setShowTransformOwnerModal(false);
    setLoading(true)
    try {
      await updateUserRole({ role: "owner", user_id: user.user_id });
      enqueueSnackbar("Transferred owner successfully", { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } });
      window.location.reload();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box>
      <UserAddModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projects={projects}
        onFinish={(items) => {
          onUsersInvite(items);
        }}
      />
      <UserRemoveModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        user={selectedUser}
        onFinish={(user) => {
          onUserDelete(user.user_id);
          setShowDeleteModal(false);
        }}
      />
      <UserRoleModal
        open={showChangeRoleModal}
        onClose={() => setShowChangeRoleModal(false)}
        user={selectedUser}
        onFinish={({ role, user }) => {
          onUserRoleUpdate(role, user);
        }}
      />
      <UserProjectsModal
        open={showProjectsModal}
        onClose={() => setShowProjectsModal(false)}
        user={selectedUser}
        projects={projects}
        onFinish={({ selectedProjects, user }) => {
          onUserProjectsUpdate(selectedProjects, user);
        }}
      />
      <UserTransferOwnerModal
        open={showTransformOwnerModal}
        onClose={() => setShowTransformOwnerModal(false)}
        user={selectedUser}
        users={confirmedUsers.filter((user) => user?.user_id !== selectedUser?.user_id && user?.user_id !== currentUser.user_id)}
        onFinish={(user) => {
          onUserTransferOwnerUpdate(user);
        }}
      />
      {!loading && <BorderWidget
        sx={{
          boxShadow: "0px 1px 3px 0px #0000001a",
          padding: {
            xs: `calc(${Constants.Styles.spacing} * 2)`,
            sm: `calc(${Constants.Styles.spacing} * 6)`,
          },
          borderRadius: `calc(${Constants.Styles.radius.base} + 4px)`,
        }}
      >
        <Stack
          direction={{
            xs: "column", sm: "row",
          }}
          spacing={2}
          alignItems="center"
          justifyContent={"space-between"}
        >
          <Box>
            <Typography sx={{
              fontWeight: Constants.Styles.fontWeight.medium,
              fontSize: Constants.Styles.fontSize.lg,
              lineHeight: Constants.Styles.lineHeight.lg,
            }}>Users</Typography>
            <Typography
              sx={{
                fontWeight: Constants.Styles.fontWeight.medium,
                fontSize: Constants.Styles.fontSize.sm,
                lineHeight: Constants.Styles.lineHeight.sm,
                color: Constants.Colors.light.mutedForeground,
                mt: "0.5rem",
              }}
            >
              Manage who has access to your Redirect Mapper account
            </Typography>
          </Box>
          {currentUserRole !== "guest" && <MainButton
            text="Add User"
            onClick={() => setIsModalOpen(true)}
            preIcon={<AddOutlinedIcon />}
            size="medium"
            fullWidth={false}
          />}
        </Stack>
        {confirmedUsers.length > 0 ? <Stack
          direction={"column"}
          spacing={"1.125rem"}
          sx={{
            mt: `calc(${Constants.Styles.spacing} * 6)`,
          }}
        >
          {confirmedUsers.map((user) => {
            return <UserItemWidget
              key={user.user_id}
              user={user}
              currentUserRole={currentUserRole}
              isLoading={loadingProgress.indexOf(user.user_id) >= 0}
              ownerTransform={(u) => {
                console.log("@@@@@ owner transform");
                setSelectedUser(u);
                setShowTransformOwnerModal(true);
              }}
              manageProjects={(u) => {
                console.log("@@@@@ manage projects");
                setSelectedUser(u);
                setShowProjectsModal(true);
              }}
              changeRole={(u) => {
                console.log("@@@@@ change role");
                setSelectedUser(u);
                setShowChangeRoleModal(true);
              }}
              removeUser={(u) => {
                setSelectedUser(u);
                setShowDeleteModal(true);
              }}
            />
          })}
        </Stack> : <Typography sx={{
          mt: `calc(${Constants.Styles.spacing} * 6)`,
        }}>No users</Typography>}
      </BorderWidget>}
      {!loading && pendingUsers.length > 0 && (
        <BorderWidget
          sx={{
            boxShadow: "0px 1px 3px 0px #0000001a",
            padding: {
              xs: `calc(${Constants.Styles.spacing} * 2)`,
              sm: `calc(${Constants.Styles.spacing} * 6)`,
            },
            borderRadius: `calc(${Constants.Styles.radius.base} + 4px)`,
            mt: `calc(${Constants.Styles.spacing} * 6)`,
          }}
        >
          <Stack
            direction={{
              xs: "column", sm: "row",
            }}
            spacing={2}
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Box>
              <Typography sx={{
                fontWeight: Constants.Styles.fontWeight.medium,
                fontSize: Constants.Styles.fontSize.lg,
                lineHeight: Constants.Styles.lineHeight.lg,
              }}>Pending Invitations</Typography>
              <Typography
                sx={{
                  fontWeight: Constants.Styles.fontWeight.medium,
                  fontSize: Constants.Styles.fontSize.sm,
                  lineHeight: Constants.Styles.lineHeight.sm,
                  color: Constants.Colors.light.mutedForeground,
                  mt: "0.5rem",
                }}
              >
                Invitations that have not been accepted yet
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction={"column"}
            spacing={"1.125rem"}
            sx={{
              mt: `calc(${Constants.Styles.spacing} * 6)`,
            }}
          >
            {pendingUsers.map((user) => {
              return <UserPendingItemWidget
                key={user.user_id}
                user={user}
                isLoading={loadingProgress.indexOf(user.user_id) >= 0}
                currentUserRole={currentUserRole}
                resendInvite={(u) => {
                  resend(u?.user_id);
                }}
                cancelUser={(u) => {
                  console.log("@@@@@ cancel pending user");
                  onUserDelete(u.user_id);
                }}
              />
            })}
          </Stack>
        </BorderWidget>
      )}
      {loading && <BorderWidget
        sx={{
          boxShadow: "0px 1px 3px 0px #0000001a",
          padding: `calc(${Constants.Styles.spacing} * 6)`,
          borderRadius: `calc(${Constants.Styles.radius.base} + 4px)`,
          mt: `calc(${Constants.Styles.spacing} * 6)`,
        }}
      >
        <Stack
          direction={"column"}
          spacing={2}
          alignItems="start"
          justifyContent={"space-between"}
        >
          <Skeleton
            variant="rounded"
            width={60}
            height={30}
          />
          <Skeleton
            variant="rounded"
            width={120}
            height={20}
          />
        </Stack>
        <Stack
          direction={"column"}
          spacing={"1.125rem"}
          sx={{
            mt: `calc(${Constants.Styles.spacing} * 6)`,
          }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <UserLoadingItemWidget key={index} id={index} />
          ))}
        </Stack>
      </BorderWidget>}
    </Box>
  );
}

export default observer(Users);
